import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash.get';
import urlJoin from 'url-join';
import { dialogs, i18next, site, template } from '@yola/ws-sdk';
import { selectors } from '@yola/subscription-manager-js';
import publishing from 'src/js/modules/publishing';
import onlinestore from 'src/js/modules/onlinestore';
import navigation from 'src/js/modules/navigation';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import config from 'src/js/modules/config';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import segment from 'src/js/modules/analytics/segment';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import getHelpMenuItemClickCallbacks from 'yola-editor/src/js/modules/editor/settings/get-help-menu-item-click-callbacks';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import yolaSite from 'yola-editor/src/js/modules/site';
import user from 'yola-editor/src/js/modules/user';
import getCurrentPackageFeatureLimits from 'yola-editor/src/js/modules/features/accessors/get-current-package-feature-limits';
import utils from 'yola-editor/src/js/router/utils';
import isFreeDomainAvailable from 'yola-editor/src/js/modules/upsell/helpers/is-free-domain-available';
import getUpsellSettings from 'yola-editor/src/js/modules/upsell/accessors/get-upsell-settings';
import getSortedItems from '../helpers/get-sorted-items';
import getComputedItems from '../helpers/get-computed-items';
import getComputedBanners from '../helpers/get-computed-banners';
import { ADVERTISING_ID, ANALYTICS_ID, HELP_MENU_ID, DESIGN_MENU_ID } from '../constants';
import components from '../components';
import getUnfinishedStepsCount from '../../site-progress-checklist/helpers/get-unfinished-steps-count';
import MainMenuItemsClickEventTracker from '../../utils/main-menu-items-click-event-tracker';
import itemIds from '../../help-menu/constants/item-ids';
import getMainMenuItems from '../helpers/get-main-menu-items';

const { NavProvider, Nav, NavToggle, NavCollapse } = components;
const {
  constants: { triggerIds },
} = segment;

function MainMenuContainer() {
  const isMultilingualSite = site.verifiers.isMultilingualSite();
  const onlineStore = useSelector(onlinestore.selectors.getOnlineStoreLimit);
  const siteURL = useSelector(yolaSite.selectors.getSiteURL);
  const activeSubscriptions = useSelector(selectors.getActiveSubscriptions);
  const allSubscriptionsList = useSelector(selectors.getSubscriptionList);
  const partnerData = useSelector(user.selectors.getPartnerData);
  const partnerId = partnerData.id;
  const isAvailableRoadmapFromPartner = get(partnerData, 'properties.wsFeatures.publicRoadmap');
  const supportUrl = useSelector(user.selectors.getSupportUrl);
  const supportEmail = useSelector(user.selectors.getSupportEmail);
  const publicRoadmapUrl = useSelector(config.selectors.getPublicRoadmapURL);
  const advertisingUrl = useSelector(config.selectors.getAdvertisingUrl);
  const analyticsUrl = useSelector(config.selectors.getAnalyticsUrl);
  const myyolaUrl = useSelector(config.selectors.getMyyolaUrl);
  const partnerHomeUrl = useSelector(user.selectors.getHomeUrl);
  const partnerDashboardUrl = useSelector(user.selectors.getPartnerDashboardUrl);
  const isWL = useSelector(user.selectors.getWhiteLabelStatus);
  const externalUpsellTypes = useSelector(user.selectors.getExternalUpsellTypes);
  const availablePlatformComponents = useSelector(user.selectors.getAvailablePlatformComponents);
  const { id: siteId } = useSelector(yolaSite.selectors.getSiteData);

  const limits = getCurrentPackageFeatureLimits();
  const { available: isAdvertisingAvailable } = limits.advertising;
  const { available: isAnalyticsAvailable } = limits.analytics;

  let redirectToDashboard;

  if (availablePlatformComponents.includes(user.platformComponents.DASHBOARD)) {
    redirectToDashboard = () => {
      utils.redirectToUrl(myyolaUrl);
    };
  }

  if (isWL && partnerDashboardUrl) {
    redirectToDashboard = () => {
      utils.redirectToUrl(partnerDashboardUrl, { topWindow: true });
    };
  }

  const redirectToAdvertising = () => {
    if (!isWL) {
      utils.redirectToUrl(urlJoin(analyticsUrl, '#advertising', siteId));
    } else {
      utils.redirectToUrl(advertisingUrl);
    }
  };

  const redirectToAnalytics = () => {
    utils.redirectToUrl(urlJoin(analyticsUrl, '#sites', siteId));
  };

  const isFooterCodeAvailable = useSelector(() =>
    template.verifiers.isVariableGlobalsAvailable(template.constants.FOOTER_CODE)
  );
  const isNavigationAvailable = useSelector(navigation.verifiers.isAvailable);
  const siteProgressChecklist = useSelector(site.selectors.getSiteProgressChecklist);

  const [featureFlags] = useFeatureFlags([
    'website_assistant',
    'refer_a_friend',
    'freedomain',
    'difm_latitude',
  ]);
  const {
    website_assistant: isWebsiteAssistantFeatureEnabled,
    refer_a_friend: isReferAFriendFeatureAvailable,
    freedomain: isFreeDomainEnabled,
    difm_latitude: isDifmLatitudeEnabled,
  } = featureFlags;

  const dispatch = useDispatch();

  const publish = useCallback(() => {
    saveSiteWithEffect(async () => {
      dispatch(publishing.actions.initiatePublishWebsite());
    });
  }, [dispatch]);

  const showPageManager = useCallback(() => {
    dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
  }, [dispatch]);
  const onWebsiteSettings = useCallback(() => {
    dispatch(dialogs.actions.show(dialogTypes.WEBSITE_SETTINGS));
  }, [dispatch]);
  const redirectToStore = useCallback(() => {
    dispatch(onlinestore.actions.redirectToStore());
  }, [dispatch]);

  const baseIconPath = 'images/main-menu-icons/';
  const items = [
    {
      title: i18next.t('Publish'),
      onClick: publish,
      image: `${baseIconPath}/publish.svg`,
      heading: i18next.t('Publish'),
      description: i18next.t('Push your site live to the Internet for the world to see!'),
      id: 'publish',
      icon: {
        glyph: 'rocket-45',
        width: 26,
        height: 26,
        strokeWidth: 1.3,
      },
    },
    {
      title: i18next.t('Page Manager'),
      onClick: showPageManager,
      image: `${baseIconPath}/page-manager.svg`,
      heading: `${i18next.t('Pages')}...`,
      description: i18next.t('Create, Delete and Manage pages of your site'),
      id: 'page-manager',
      icon: {
        glyph: 'layers',
        width: 20,
        height: 20,
        strokeWidth: 1.5,
      },
    },
  ];

  if (isNavigationAvailable) {
    items.push({
      title: i18next.t('Navigation manager'),
      onClick: (e) => {
        navigation.operations.openNavigationExtension(e.target, 'right');
      },
      heading: `${i18next.t('Navigation')}...`,
      description: i18next.t('Manage the navigation menu of your site'),
      id: 'navigation',
      icon: {
        glyph: 'hierarchy',
        width: 22,
        height: 22,
        strokeWidth: 1.1,
      },
    });
  }

  items.push({
    title: i18next.t('Settings'),
    onClick: onWebsiteSettings,
    heading: `${i18next.t('Settings')}...`,
    description: i18next.t('Configure your site'),
    id: 'settings',
    icon: {
      glyph: 'settings',
      width: 20,
      height: 20,
      strokeWidth: 1.5,
    },
  });

  if (onlineStore.available) {
    items.push({
      heading: i18next.t('Online Store'),
      onClick: redirectToStore,
      order: 80,
      id: 'online-store',
      icon: {
        glyph: 'online-store',
        width: 24,
        height: 24,
        strokeWidth: 1.3,
      },
    });
  }

  const hasAvailableFreeDomain = isFreeDomainAvailable(
    activeSubscriptions,
    allSubscriptionsList,
    isFreeDomainEnabled
  );

  const isComplianceServiceMenuItemEnabled = externalUpsellTypes.includes(
    user.constants.externalUpsellTypes.DIFM_PRIVACY
  );

  const upsells = getUpsellSettings({
    isFreeDomainAvailable: hasAvailableFreeDomain,
  });

  const isOrderWebsiteMenuItemEnabled =
    isDifmLatitudeEnabled &&
    (!isWL || externalUpsellTypes.includes(user.constants.externalUpsellTypes.DIFM_SERVICE));

  const mainMenu = getMainMenuItems({
    siteURL,
    partnerId,
    supportUrl,
    supportEmail,
    publicRoadmapUrl: isAvailableRoadmapFromPartner ? publicRoadmapUrl : null,
    isReferAFriendFeatureAvailable: !isWL && isReferAFriendFeatureAvailable,
    redirectToAnalytics: isAnalyticsAvailable ? redirectToAnalytics : null,
    redirectToAdvertising: isAdvertisingAvailable ? redirectToAdvertising : null,
    redirectToDashboard,
    redirectToWebsite() {
      redirectToUrl(partnerHomeUrl, { topWindow: true });
    },
    upsells,
    partnerProperties: partnerData.properties,
    isFreeDomainAvailable: hasAvailableFreeDomain,
    isComplianceServiceMenuItemEnabled,
    isOrderWebsiteMenuItemEnabled,
  });

  const mainMenuItems = get(mainMenu, 'items', []);
  const mainMenuBanners = get(mainMenu, 'banners', []);

  const itemsFromSettings = mainMenuItems.filter((item) => {
    if (item.id === ADVERTISING_ID || item.id === ANALYTICS_ID) {
      // if site does not have footer_code variable and global, analytics and advertising addons won't work
      return isFooterCodeAvailable;
    }

    return true;
  });

  const sortedItems = getSortedItems(items, itemsFromSettings);
  const helpMenuItem = sortedItems.find((item) => item.id === HELP_MENU_ID);
  const helpMenuItems = get(helpMenuItem, 'subMenu.submenu', []);

  const designMenuItem = sortedItems.find((item) => item.id === DESIGN_MENU_ID);
  const designMenuItems = get(designMenuItem, 'subMenu.submenu', []);

  let multilingualTourMenuItem = {};
  const { multilingualTourOnClick } = getHelpMenuItemClickCallbacks(triggerIds.MAIN_MENU);

  if (isMultilingualSite) {
    multilingualTourMenuItem = {
      title: i18next.t('Multilingual tour'),
      heading: i18next.t('Multilingual tour'),
      id: itemIds.MULTILINGUAL_TOUR,
      description: '',
      order: 4,
      onClick: multilingualTourOnClick,
    };
  }

  const menuSegments = {
    items: sortedItems,
    helpMenuItems: [...helpMenuItems, multilingualTourMenuItem],
    designMenuItems: [...designMenuItems],
  };

  const menu = getComputedItems(menuSegments);
  const banners = getComputedBanners(mainMenuBanners);

  const steps = Object.values(siteProgressChecklist).length;
  const stepsCompleted = steps - getUnfinishedStepsCount(siteProgressChecklist);
  const checklistProgressData = {
    isEnabled: isWebsiteAssistantFeatureEnabled,
    heading: 'Set up your site',
    title: `${stepsCompleted} of ${steps} completed`,
    steps,
    stepsCompleted,
    onClick: () => {
      dialogs.operations.show(dialogTypes.SITE_PROGRESS_CHECKLIST);
    },
  };

  return (
    <NavProvider>
      <Nav>
        <NavToggle />
        <MainMenuItemsClickEventTracker items={menu}>
          <NavCollapse
            items={menu}
            banners={banners}
            checklistProgressData={checklistProgressData}
          />
        </MainMenuItemsClickEventTracker>
      </Nav>
    </NavProvider>
  );
}

export default MainMenuContainer;
